<template>
  <div
    ref="chromeRef"
    aria-hidden="true"
    class="pointer-events-none absolute inset-x-0 top-0 z-[8] h-80"
    :style="{
      background:
        'linear-gradient(to bottom, rgba(255, 255, 255, 0.01) 50%, transparent 100%)',
    }"
  />
  <div
    aria-hidden="true"
    class="pointer-events-none absolute inset-x-0 top-0 z-[9] h-64"
    :style="{
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
      maskImage: 'linear-gradient(to bottom, black 83%, transparent 100%)',
      WebkitMaskImage:
        'linear-gradient(to bottom, black 55%, transparent 100%)',
    }"
  />
  <div
    aria-hidden="true"
    class="pointer-events-none absolute inset-x-0 top-0 z-[9] h-80"
    :style="{
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
      maskImage: 'linear-gradient(to bottom, black 65%, transparent 100%)',
      WebkitMaskImage:
        'linear-gradient(to bottom, black 55%, transparent 100%)',
    }"
  />

  <header class="absolute flex items-center justify-between p-16 w-full z-10">
    <RevButtonIcon
      v-if="showNewChatButton"
      :aria-label="i18n(translations.newChat)"
      :icon="IconPlusInCircle"
      size="small"
      variant="ghost"
      @click="handleNewChatClick"
    />
    <RevButtonIcon
      v-if="hasHistory"
      :aria-label="i18n(translations.chatHistoryButton)"
      :icon="IconHistory"
      size="small"
      variant="ghost"
      @click="handleChatHistoryClick"
    />
    <div class="flex">
      <h1 class="heading-3">{{ i18n(translations.title) }}</h1>
      <IconSparklesFilled size="small" />
    </div>

    <RevButtonIcon
      :aria-label="i18n(translations.closeChat)"
      :icon="IconCrossSmall"
      size="small"
      variant="ghost"
      @click="handleCloseClick"
    />
  </header>
</template>

<script lang="ts" setup>
import { computed, useTemplateRef } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { openModal } from '@ds/components/ModalBase'
import { IconCrossSmall } from '@ds/icons/IconCrossSmall'
import { IconHistory } from '@ds/icons/IconHistory'
import { IconPlusInCircle } from '@ds/icons/IconPlusInCircle'
import { IconSparklesFilled } from '@ds/icons/IconSparklesFilled'

import { useFetchShoppingAssistantChatHistory } from '~/scopes/shopping-assistant/composables/useFetchShoppingAssistantChatHistory'
import { useNewConversationDialog } from '~/scopes/shopping-assistant/composables/useNewConversationDialog'
import { useShoppingAssistantHost } from '~/scopes/shopping-assistant/composables/useShoppingAssistantHost'
import { CHAT_HISTORY_DIALOG_NAME } from '~/scopes/shopping-assistant/constants'

import translations from './Header.translations'

const emit = defineEmits<{
  (e: 'click', payload: { name: string }): void
  (e: 'new-chat'): void
}>()

const i18n = useI18n()

const { close } = useShoppingAssistantHost()
const { requestNewChat } = useNewConversationDialog()

const {
  data,
  pending,
  refresh: refreshHistory,
} = useFetchShoppingAssistantChatHistory()

// Optimistic: show the "new chat" button while pending or when there is no
// history. Once the fetch resolves with results, swap to the "history" button.
const hasHistory = computed(() => (data.value?.results.length ?? 0) > 0)
const showNewChatButton = computed(() => pending.value || !hasHistory.value)

// The bottom edge of this element marks where chat content can sit without
// being obscured by the fading header chrome. Exposed for layout math in the parent.
const chromeRef = useTemplateRef<HTMLElement>('chromeRef')
defineExpose({ chromeEl: chromeRef })

function handleCloseClick() {
  close()
  emit('click', { name: 'close_chat' })
}

function handleNewChatClick() {
  requestNewChat(() => {
    emit('new-chat')
    emit('click', { name: 'new_chat' })
  })
}

function handleChatHistoryClick() {
  refreshHistory()
  openModal(CHAT_HISTORY_DIALOG_NAME)
  emit('click', { name: 'chat_history' })
}
</script>
